import { create } from 'zustand';
import { ApiError, backend, type User } from '../api/backend';

interface SessionState {
  user: User | null;
  /** 'unknown' until /api/auth/me resolves */
  status: 'unknown' | 'anonymous' | 'authenticated' | 'offline';
  error: string | null;
  init(): Promise<void>;
  login(username: string, password: string): Promise<boolean>;
  register(username: string, password: string): Promise<boolean>;
  logout(): Promise<void>;
}

function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: string; detail?: string } | null;
    return body?.error ?? body?.detail ?? `Request failed (${e.status})`;
  }
  return e instanceof Error ? e.message : 'Request failed';
}

export const useSession = create<SessionState>((set) => ({
  user: null,
  status: 'unknown',
  error: null,

  async init() {
    try {
      const { user } = await backend.auth.me();
      set({ user, status: user ? 'authenticated' : 'anonymous', error: null });
    } catch {
      // backend down — let the user into the UI shell anyway
      set({ user: null, status: 'offline', error: null });
    }
  },

  async login(username, password) {
    try {
      const { user } = await backend.auth.login(username, password);
      set({ user, status: 'authenticated', error: null });
      return true;
    } catch (e) {
      set({ error: errorMessage(e) });
      return false;
    }
  },

  async register(username, password) {
    try {
      const { user } = await backend.auth.register(username, password);
      set({ user, status: 'authenticated', error: null });
      return true;
    } catch (e) {
      set({ error: errorMessage(e) });
      return false;
    }
  },

  async logout() {
    try {
      await backend.auth.logout();
    } finally {
      set({ user: null, status: 'anonymous' });
    }
  },
}));
